import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { Toaster as Sonner, type ToasterProps } from "sonner";

import { useAppearance } from "./appearance-provider.js";

/**
 * Phase E1.b.1 — IA + SA + PA shared toaster.
 *
 * Adapted from the canonical shadcn `sonner` block. The original block depends
 * on `next-themes`'s `useTheme()` which doesn't exist in a Vite SPA, so we
 * read the resolved (light | dark) mode from our own `<AppearanceProvider>` —
 * `mode` would be `system` and sonner can't render that. All three apps
 * (IA / SA / PA) already mount `<AppearanceProvider>` at the root.
 *
 * Iron Rule #7 — Single primitive surface. Consumers MUST import `toast`
 * from `@eduviaos/ui-kit`, never directly from `sonner`. The barrel
 * (`packages/ui-kit/src/index.ts`) re-exports it.
 *
 * Iron Rule #16 — Animation budget. sonner respects `prefers-reduced-motion`
 * natively (no extra wiring needed). Default duration is 4s; success/info
 * use 4s, error/warning 8s — handled at call sites via `toast.error(...,
 * { duration: 8000 })` when actionable.
 *
 * Token wiring: sonner reads `--normal-bg`, `--normal-text`,
 * `--normal-border`, `--border-radius` from this component's inline style.
 * They point at the design-token variables (`--color-card` etc. — the
 * popover surface is the card surface in every app's `@theme`), so toasts
 * inherit the active palette and dark mode.
 *
 * The loading icon is a block-level 16px box: sonner centres
 * `.sonner-loader` with `translate(-50%, -50%)`, so an inline SVG (whose
 * line box is taller than the glyph) would spin around a point below its
 * centre and appear to orbit. `eduviaos-spin` rotates about the box centre.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { resolvedMode } = useAppearance();

  return (
    <Sonner
      theme={resolvedMode}
      className="toaster group"
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: (
          <span className="block size-4" data-testid="toast-loading-icon">
            <Loader2Icon className="eduviaos-spin block size-4" />
          </span>
        ),
      }}
      style={
        {
          "--normal-bg": "var(--color-card)",
          "--normal-text": "var(--color-card-foreground)",
          "--normal-border": "var(--color-border)",
          "--border-radius": "var(--radius-md)",
        } as React.CSSProperties
      }
      {...props}
    />
  );
};

export { Toaster };
export { toast } from "sonner";
